<!-- Responsibility: Implement npc card behavior for the ui/components surface.
Scope: Owns ui/components concerns only; cross-surface orchestration is excluded. -->
<npc-card>
  <div
    class="npc-chip tooltip-wrap { isCompact() ? 'compact' : '' } { state.selected ? 'selected' : '' }"
    data-selected={ state.selected ? 'true' : 'false' }
    data-e2e-open="npc-inspect"
    role="button"
    tabindex="0"
    aria-label={ ariaLabel() }
    data-tip-lock={ props.showTooltip === false ? 'off' : null }
    onclick={ openDetails }
    onkeydown={ onKeydown }
  >
    <header>
      <b>{ compactName() }</b>
      <span class="badge { relationTone() }">{ relationBadgeText() }</span>
    </header>

    <div class="identity" if={ !isCompact() }>{ props.npc.identity || 'Unknown' }</div>

    <div class="meta" if={ !isCompact() }>
      <span class="badge">Mem { memoryCount() }</span>
      <span class="badge intent" if={ intentLabel() }>{ intentLabel() }</span>
    </div>

    <div class="effects" if={ !isCompact() && hasVisibleEffects() }>
      <span each={ fx in activeFxPreview() } class="badge active">{ effectChipText(fx) }</span>
      <span each={ fx in dormantFxPreview() } class="badge dormant">{ effectChipText(fx, true) }</span>
      <span class="badge more" if={ hiddenFxCount() > 0 }>+{ hiddenFxCount() } more</span>
    </div>

    <span class="action-hint" if={ !isCompact() }>Inspect</span>

    <!-- HTML-First Tooltip -->
    <div class="tooltip" data-position="top" if={ shouldRenderCompactTooltip() } data-tip-custom="true">
      <raw-html content={ compactTooltip() } />
    </div>
  </div>

  <script>
    import { dispatcher } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { effectChipText as formatEffectChipText } from '../helpers/effect-chip.js'
    import { openNpcDialog } from '../dialog/dialog-openers.ts'

    export default {
      state: { selected: false },

      onMounted() {
        this.modalCloseHandle = dispatcher.on(TOPICS.ui.dialogClose, () => this.clearSelected())
      },

      onUnmounted() {
        if (this.modalCloseHandle) dispatcher.off(this.modalCloseHandle)
        if (this.selectedTimer) clearTimeout(this.selectedTimer)
      },

      isCompact() {
        return this.props.compact === true || this.props.compact === 'true'
      },

      shouldRenderCompactTooltip() {
        return this.isCompact() && this.props.showTooltip !== false
      },

      compactName() {
        const raw = String(this.props.npc?.name ?? '').trim()
        if (!raw) return 'NPC'
        if (!this.isCompact()) return raw
        const first = raw.split(/\s+/).filter(Boolean).slice(0, 2).join(' ')
        return this.middleTruncate(first, 16)
      },

      middleTruncate(value, max = 16) {
        const text = String(value ?? '').trim()
        if (!text) return ''
        if (text.length <= max) return text
        const visible = Math.max(4, max - 1)
        const head = Math.ceil(visible / 2)
        const tail = Math.floor(visible / 2)
        return `${text.slice(0, head)}…${text.slice(-tail)}`
      },

      activeEffectCount() {
        const effects = this.props.npc?.activeEffects ?? []
        return [...new Set(effects.filter(Boolean))].length
      },

      relationshipValue() {
        return Number.isFinite(this.props.npc?.relationship) ? this.props.npc.relationship : 0
      },

      relationshipSigned() {
        const value = this.relationshipValue()
        return value > 0 ? `+${value}` : `${value}`
      },

      relationTone() {
        const rel = this.relationshipValue()
        if (rel >= 40) return 'friendly'
        if (rel <= -40) return 'hostile'
        return 'neutral'
      },

      relationLabel() {
        const rel = this.relationshipValue()
        if (rel >= 80) return 'Devoted'
        if (rel >= 50) return 'Friendly'
        if (rel >= 20) return 'Neutral'
        if (rel >= -20) return 'Wary'
        if (rel >= -50) return 'Hostile'
        return 'Enemy'
      },

      relationCompactLabel() {
        const rel = this.relationshipValue()
        if (rel >= 40) return 'Ally'
        if (rel <= -40) return 'Foe'
        return 'Neutral'
      },

      relationBadgeText() {
        if (this.isCompact()) return `REL ${this.relationshipSigned()}`
        return `${this.relationLabel()} ${this.relationshipSigned()}`
      },

      relationDetailText() {
        return `${this.relationLabel()} relationship ${this.relationshipSigned()}`
      },

      activeFxLabels() {
        return [...new Set((this.props.npc?.activeEffects ?? []).map(v => String(v ?? '').trim()).filter(Boolean))]
      },

      dormantFxLabels() {
        return [...new Set((this.props.npc?.dormantEffects ?? []).map(e => e?.label || e?.source || 'Effect').map(v => String(v ?? '').trim()).filter(Boolean))]
      },

      effectChipText(label, dormant = false) {
        return formatEffectChipText(label, dormant)
      },

      hasVisibleEffects() {
        return this.activeFxLabels().length > 0 || this.dormantFxLabels().length > 0
      },

      activeFxPreview() {
        return this.activeFxLabels().slice(0, 2)
      },

      dormantFxPreview() {
        const remaining = Math.max(0, 3 - this.activeFxPreview().length)
        return this.dormantFxLabels().slice(0, remaining)
      },

      hiddenFxCount() {
        const total = this.activeFxLabels().length + this.dormantFxLabels().length
        const shown = this.activeFxPreview().length + this.dormantFxPreview().length
        return Math.max(0, total - shown)
      },

      memoryCount() {
        return Array.isArray(this.props.npc?.memory) ? this.props.npc.memory.length : 0
      },

      intentLabel() {
        const first = Array.isArray(this.props.npc?.intentions) ? this.props.npc.intentions[0] : ''
        const raw = String(first ?? '').trim()
        if (!raw) return ''
        const normalized = raw
          .toLowerCase()
          .replace(/\b\w/g, ch => ch.toUpperCase())
        return normalized.length > 20 ? `${normalized.slice(0, 19)}…` : normalized
      },

      compactTooltip() {
        if (!this.isCompact()) return ''
        const name = String(this.props.npc?.name ?? 'NPC').trim() || 'NPC'
        const relation = this.relationDetailText()
        const intent = this.intentLabel()
        
        let html = `<b>NPC</b> ${name}<br/>`
        html += `<b>Relation</b> ${relation}`
        if (intent) html += `<br/><b>Intent</b> ${intent}`
        
        return html
      },

      ariaLabel() {
        const name = String(this.props.npc?.name ?? 'NPC').trim() || 'NPC'
        return `Inspect NPC ${name}. Relationship ${this.relationshipSigned()}. Action opens NPC details and known effects.`
      },

      markSelected() {
        if (this.selectedTimer) clearTimeout(this.selectedTimer)
        this.update({ selected: true })
        this.selectedTimer = setTimeout(() => this.update({ selected: false }), 2400)
      },

      clearSelected() {
        if (this.selectedTimer) clearTimeout(this.selectedTimer)
        this.update({ selected: false })
      },

      async openDetails() {
        this.markSelected()
        const npc = this.props.npc
        await openNpcDialog('npc-card', npc)
      },

      onKeydown(e) {
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        this.openDetails()
      },
    }
  </script>

  <style>
    .npc-chip {
      position: relative;
      display: grid !important;
      gap: var(--space-xs);
      padding: var(--space-xs);
      min-width: min(11.25rem, 100%);
      align-items: start !important;
      border-radius: var(--radius-sm);
      background: linear-gradient(
        180deg,
        var(--chip-npc-surface-a),
        var(--chip-npc-surface-b)
      );
      border: 1px solid var(--chip-npc-border);
      box-shadow: var(--shadow-elev-1);
      transition: border-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;
      cursor: pointer;
    }

    .npc-chip.compact {
      gap: var(--space-xs);
      min-width: 10.4rem;
      width: fit-content;
      max-width: clamp(10.8rem, 24vw, 13.6rem);
      min-height: var(--room-chip-height);
      padding: 0 var(--space-xs);
      display: flex !important;
      align-items: center !important;
    }

    .npc-chip:hover {
      border-color: var(--gold);
      transform: translateY(-1px);
      box-shadow: var(--shadow-elev-2);
    }
    .npc-chip.selected {
      border-color: var(--gold);
      box-shadow: 0 0 0 1px var(--gold-glow), var(--shadow-elev-3);
      transform: translateY(-1px);
      animation: chip-selected-pulse 0.65s ease-out;
    }
    .npc-chip:active {
      animation: chip-press-pop 0.2s ease-out;
    }
    .npc-chip:focus-visible {
      outline: 0.125rem solid var(--gold);
      outline-offset: 0.125rem;
      box-shadow: var(--shadow-focus-ring);
    }

    .npc-chip > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-xs);
      line-height: 1.14;

      .npc-chip.compact & {
        gap: var(--space-xs);
        width: 100%;
      }
    }

    .npc-chip.compact b {
      font-size: var(--space-sm);
      letter-spacing: 0.01em;
      color: color-mix(in srgb, #fee2e2 var(--alpha-high), var(--text-prose));
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .badge {
      font-size: var(--type-3xs);
      letter-spacing: 0.04em;
      padding: 0.05rem var(--space-xs);
      border: 1px solid var(--border);
      border-radius: var(--radius-pill, 62.4375rem);
      color: var(--text-dim);
      background: var(--surface-card-3);
      font-weight: 650;
    }

    .npc-chip.compact .badge {
      font-size: var(--type-3xs);
      padding: 0.06rem var(--space-xs);
      letter-spacing: 0.045em;
    }

    .badge.friendly { color: var(--sem-success-fg); border-color: var(--sem-success-border); background: var(--sem-success-bg); }
    .badge.hostile  { color: var(--sem-danger-fg); border-color: var(--sem-danger-border); background: var(--sem-danger-bg); }

    .identity {
      color: var(--text-muted);
      font-size: var(--type-3xs);
      letter-spacing: 0.09em;
      line-height: 1.1;
      opacity: 0.9;
    }

    .meta, .effects {
      display: flex;
      align-items: center;
      gap: var(--space-xs);
      flex-wrap: wrap;
    }

    .meta .badge {
      border-color: var(--alpha-mid);
      color: var(--text-muted);
    }

    .meta .badge.intent {
      border-color: var(--sem-danger-border);
      color: var(--sem-danger-fg);
      background: var(--sem-danger-bg);
      max-width: 7.8125rem;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    .effects .badge.active {
      color: var(--gold);
      border-color: var(--sem-warn-border);
      background: var(--sem-warn-bg);
      box-shadow: 0 0 0 1px var(--gold-glow);
      animation: fx-active-pulse 2.5s ease-in-out infinite;
    }
    .effects .badge.dormant {
      color: var(--brand-primary);
      border-color: var(--sem-info-border);
      background: var(--sem-info-bg);
      opacity: 0.88;
    }
    .effects .badge.more {
      color: var(--text-dim);
      border-color: var(--border);
      background: var(--surface-card-2);
    }

    .action-hint {
      margin-left: auto;
      font-size: 0.64rem;
      color: var(--gold);
      letter-spacing: 0.04em;
    }

    .npc-chip.compact .badge {
      font-size: var(--type-3xs);
      padding: 0.02rem 0.18rem;
    }

  </style>
</npc-card>
